import React, { useState } from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import _ from 'lodash';
import { Typography } from '@mui/material';
import { CreatePasswordField } from '../../inputs/text-fields/passwords/CreatePasswordField';
import { PasswordField } from '../../inputs/text-fields/passwords/PasswordField';
import { TextField } from '../../inputs';
import CBButton from '../../Buttons/CbButton';
import { validatePassword } from '../../../api/SignupService';

interface SignupEmailExistingProps {
  readonly data?: any;
  readonly token?: any;
  readonly redirectUrl?: any;
  readonly submitJoinExistingAccount: (
    payload: any,
    token: any
  ) => Promise<any>;
}

function SignupEmailExisting({
  data,
  token,
  redirectUrl,
  submitJoinExistingAccount,
}: Readonly<SignupEmailExistingProps>) {
  const params = useParams<{ token?: string }>();
  const [firstName, setFirstName] = useState(data ? data.firstName : '');
  const [lastName, setLastName] = useState(data ? data.lastName : '');
  const [email] = useState(data ? data.email : '');
  const [tokenType] = useState(data ? data.tokenType : '');
  const [deepLink, setDeepLink] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [errors, setErrors] = useState<any>({
    password1: [],
    password2: undefined,
    generalNetwork: undefined,
  });
  const [disabled, setDisabled] = useState(false);

  const runCheckPasswords = (p1: string, p2: string) => {
    if (p1 !== p2) {
      setErrors({ password2: 'Passwords do not match' });
    } else {
      setErrors({ password2: undefined });
    }
  };

  const success = () => {
    if (_.isUndefined(redirectUrl)) {
      setRedirect(true);
      setDisabled(false);
      setDeepLink(false);
    } else {
      setDeepLink(true);
      setRedirect(false);
    }
  };

  const error = (err: any) => {
    if (err.response.status === 400) {
      setErrors({ password1: Object.values(err.response.data.errors) });
      setDisabled(false);
    } else if (err.response.status === 404) {
      setErrors({ generalNetwork: 'Email Setup Link Expired/Not Found!' });
      setDisabled(false);
    } else if (err.response.status === 412) {
      const errorMessage =
        typeof err.response.data == 'string'
          ? err.response.data
          : 'Authorization Failed for Token';
      setErrors({ generalNetwork: errorMessage });
      setDisabled(false);
    } else {
      setErrors({ generalNetwork: 'Service Unavailable. Please try again.' });
      setDisabled(false);
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();

    runCheckPasswords(password1, password2);
    if (password1 !== password2) {
      return;
    }

    const payload = {
      firstName,
      lastName,
      password: password1,
    };

    setDisabled(true);

    submitJoinExistingAccount(payload, token)
      .then(() => success())
      .catch((err: any) => error(err));
  };

  const handleChange = (name: string) => (event: any) => {
    const value = event.target?.value;
    if (name === 'firstName') {
      setFirstName(value);
    } else if (name === 'lastName') {
      setLastName(value);
    } else if (name === 'password2') {
      setPassword2(value);
    }
    const p1 = name === 'password1' ? value : password1;
    const p2 = name === 'password2' ? value : password2;
    runCheckPasswords(p1, p2);
  };

  const handlePasswordOnChange = (field: string) => (value: any) => {
    if (field === 'password1') {
      setPassword1(value);
    } else if (field === 'password2') {
      setPassword2(value);
    }
    const p1 = field === 'password1' ? value : password1;
    const p2 = field === 'password2' ? value : password2;
    runCheckPasswords(p1, p2);
  };

  const handleError = (BEValidationErrors: any) => {
    setErrors((curr: any) => ({
      password1: BEValidationErrors,
      password2: curr.password2,
    }));
  };

  const handleValidatePassword = (password: any) => {
    return validatePassword({ token: params.token }, { password });
  };

  if (deepLink) {
    return <Redirect to={redirectUrl} />;
  }
  if (redirect) {
    return <Redirect to="/loading" />;
  }

  const showNameFields =
    !_.eq(tokenType, 'QUOTE_SENT_NEW_USER') &&
    !_.eq(tokenType, 'QUOTE_LINK_NEW_AGENT');

  return (
    <div style={{ textAlign: 'left' }}>
      <form onSubmit={handleSubmit}>
        {_.eq(tokenType, 'QUOTE_SENT_NEW_USER') && (
          <Typography variant="body1" color="default" align="center">
            Please create a password for {email}
          </Typography>
        )}
        {_.eq(tokenType, 'QUOTE_LINK_NEW_AGENT') && (
          <Typography variant="body1" color="default" align="center">
            Please create a password for {email}
          </Typography>
        )}
        {showNameFields && (
          <span>
            <Typography variant="body1" color="default" align="center">
              Please enter below information to complete your{' '}
              {data.accountType === 'AGENCY' ? 'agency' : 'account'} setup!
            </Typography>

            <TextField
              id="firstName"
              label="First Name"
              required
              value={firstName}
              onChange={handleChange('firstName')}
            />

            <TextField
              id="lastName"
              label="Last Name"
              required
              value={lastName}
              onChange={handleChange('lastName')}
            />
          </span>
        )}
        <CreatePasswordField
          // @ts-expect-error CreatePasswordField is a forwardRef without typed props
          id="password1"
          label="Password"
          required
          errors={errors.password1}
          onChange={handlePasswordOnChange('password1')}
          onError={({ errors: passwordErrors }: any) =>
            handleError(passwordErrors)
          }
          onValidate={handleValidatePassword}
        />
        <div style={{ paddingTop: '.8rem' }}>
          <PasswordField
            // @ts-expect-error PasswordField is a forwardRef without typed props
            id="password2"
            name="password2"
            label="Confirm Password"
            value={password2}
            required
            onChange={handleChange('password2')}
            error={errors.password2}
          />
          <span className="error-text">{errors.password2}</span>
        </div>
        <span className="error-text">{errors.generalNetwork}</span>
        <div style={{ textAlign: 'center', marginTop: '15px' }}>
          <CBButton
            loading={disabled}
            disabled={disabled}
            type="submit"
            styleName="ctaButton"
            buttonText="Create account"
            size="large"
          />
        </div>
      </form>
      <Typography align="center" style={{ marginTop: 15 }}>
        Have an account? <Link to="/login">Sign in</Link>
      </Typography>
      {errors.generalNetwork && (
        <Typography align="center" style={{ paddingTop: '10px', color: 'red' }}>
          {errors.generalNetwork}
        </Typography>
      )}
    </div>
  );
}

export default SignupEmailExisting;
